/**
 * Canonical draft status values. Wire format matches the backend's Prisma
 * `DraftStatus` enum exactly, so these values can be sent on the wire and
 * read from `DraftDto.status` without any transform.
 *
 * Source of truth in the design system:
 *   magpiie-ui-ux/ui_kits/app/RevisionsProject.jsx → STATUS_MAP
 *
 * Pattern mirrors `constants/issueStatus.ts` — string-valued enum so the
 * members can be passed straight to the API.
 */
import type { StatusPillVariant } from '../component/StatusPill/StatusPill';

export enum DraftStatus {
  DRAFT = 'DRAFT',
  EDITING = 'EDITING',
  IN_REVIEW = 'IN_REVIEW',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  APPROVED = 'APPROVED',
}

export const DRAFT_STATUS_LABELS: Record<DraftStatus, string> = {
  [DraftStatus.DRAFT]: 'Draft',
  [DraftStatus.EDITING]: 'Editing',
  [DraftStatus.IN_REVIEW]: 'In Review',
  [DraftStatus.CHANGES_REQUESTED]: 'Changes Requested',
  [DraftStatus.APPROVED]: 'Approved',
};

export const DRAFT_STATUS_TO_VARIANT: Record<DraftStatus, StatusPillVariant> = {
  [DraftStatus.DRAFT]: 'draft',
  [DraftStatus.EDITING]: 'editing',
  [DraftStatus.IN_REVIEW]: 'review',
  [DraftStatus.CHANGES_REQUESTED]: 'changes',
  [DraftStatus.APPROVED]: 'approved',
};

// Display order — used by the inline status picker on draft cards and by the
// status filter on the project screen. Mirrors the DS STATUS_MAP order.
export const DRAFT_STATUS_ORDER: DraftStatus[] = [
  DraftStatus.DRAFT,
  DraftStatus.EDITING,
  DraftStatus.IN_REVIEW,
  DraftStatus.CHANGES_REQUESTED,
  DraftStatus.APPROVED,
];

export const draftStatusToVariant = (
  status: DraftStatus | undefined | null
): StatusPillVariant | null =>
  status ? DRAFT_STATUS_TO_VARIANT[status] : null;

/**
 * Coerce the wire string returned by the API into the DraftStatus enum.
 * The wire format already matches enum values 1:1; this just narrows the
 * type so call sites can use enum members instead of string literals.
 */
export const toDraftStatus = (
  raw: string | undefined | null
): DraftStatus | undefined => {
  if (!raw) return undefined;
  return Object.values(DraftStatus).includes(raw as DraftStatus)
    ? (raw as DraftStatus)
    : undefined;
};
